import { useEffect, useMemo, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';

import type { IAgentAttendance, IScheduleStatus } from '@/components/agent/dashboard/types';
import type { ScheduleId } from '@/types/schedule';

import { useAppSelector, useTimezone, useWeek } from '@/hooks';
import { getShiftUnmet, isKnownError, logError, makeCancellable } from '@/helpers';
import { SCHEDULE_STATUS_BASE_LIST } from '@/constants';
import { apiContract } from '@/api/contract';
import { selectSchedulesList } from '@/store/selectors/scheduleSelectors';
import { selectUserId } from '@/store/selectors/authSelectors';
import { CachedBlock } from '@/classes/cachedBlock';

interface IReturn {
  schedules: IScheduleStatus[];
  loadingAttendance: boolean;
}

const scheduleStatusSelector = createSelector(selectSchedulesList, selectUserId, (schedulesList, userId) => ({
  schedulesList,
  userId,
}));

const useScheduleStatus = (): IReturn => {
  const { schedulesList, userId } = useAppSelector(scheduleStatusSelector);
  const [attendanceData, setAttendanceData] = useState<Record<ScheduleId, IAgentAttendance[]>>({});
  const [loadingAttendance, setLoadingAttendance] = useState<IReturn['loadingAttendance']>(false);
  const { currentWeek } = useWeek();
  const { timezoneName } = useTimezone();

  useEffect(() => {
    if (!userId || !currentWeek) return;

    setLoadingAttendance(true);

    const abortController = new AbortController();
    const getAgentAttendanceRequest = makeCancellable(apiContract.getAgentAttendance, abortController.signal);
    const promises = [
      getAgentAttendanceRequest({ weekId: currentWeek - 1, userId }),
      getAgentAttendanceRequest({ weekId: currentWeek, userId }),
      getAgentAttendanceRequest({ weekId: currentWeek + 1, userId }),
    ];
    Promise.all(promises)
      .then(responses => {
        const data = responses.flatMap(res => {
          if (isKnownError(res.data)) return [];
          return res.data;
        });
        const attendanceData = data
          .flatMap(item => item)
          .reduce<Record<ScheduleId, IAgentAttendance[]>>((acc, item) => {
            acc[item.schedule_id] = acc[item.schedule_id] ? acc[item.schedule_id] : [];
            if (item.user_id === userId) acc[item.schedule_id].push(item);
            return acc;
          }, {});

        setAttendanceData(attendanceData);
      })
      .catch(e => {
        logError(e);
      })
      .finally(() => {
        setLoadingAttendance(false);
      });

    return () => {
      setLoadingAttendance(false);
      abortController.abort();
    };
  }, [currentWeek, userId]);

  const schedules = useMemo<IReturn['schedules']>(() => {
    const currentLocalBlock = CachedBlock.fromMillis(Date.now(), timezoneName);
    return schedulesList.map(schedule => {
      const data = { ...SCHEDULE_STATUS_BASE_LIST() };
      const attendance = attendanceData?.[schedule.scheduleId] ?? [];
      schedule.shifts.forEach(shift => {
        const { startBlock, endBlock } = shift;
        const properKey = Object.keys(data).find(
          key => data[key].startBlock <= startBlock && data[key].endBlock >= startBlock,
        );
        if (!properKey) return;
        const shiftDuration = (shift.endBlock + 1 - shift.startBlock) / 2;
        const isFutureShift = endBlock > currentLocalBlock.blockId;

        data[properKey] = {
          ...data[properKey],
          total: data[properKey].total + shiftDuration,
          unmet: getShiftUnmet(shift.shiftId, shiftDuration, attendance, data[properKey].unmet, isFutureShift),
        };
      });

      return {
        clientName: schedule.clientName,
        scheduleName: schedule.scheduleName,
        scheduleId: schedule.scheduleId,
        data,
      } as IScheduleStatus;
    });
  }, [attendanceData, schedulesList, timezoneName]);

  return {
    schedules,
    loadingAttendance,
  };
};

export { useScheduleStatus };
